import { SETTINGS, settingDef, settingProblem, splitSettingKey, type SettingSource, type SettingValue, type SettingsMap } from '@lp/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Input, Select } from './ui';

export const SOURCE_LABEL: Record<SettingSource, string> = { platform: 'Platform default', package: 'Package', template: 'Template', company: 'Company' };
const SOURCE_TONE = { platform: 'gray', package: 'blue', template: 'amber', company: 'green' } as const;

/** where a value comes from, as a small badge */
export function SourceBadge({ source }: { source: SettingSource }) {
  const t = useT();
  return <Badge tone={SOURCE_TONE[source]}>{t(SOURCE_LABEL[source])}</Badge>;
}

/** a setting's value as the owner reads it: null = the product's own rule, '' = nothing */
export function SettingValueText({ settingKey, value }: { settingKey: string; value: SettingValue }) {
  const t = useT();
  const def = settingDef(settingKey);
  if (value == null) return <span className="text-ink-500">{t('The product’s own rule')}</span>;
  if (value === '') return <span className="text-ink-400">—</span>;
  return <>{def?.kind === 'int' ? num(Number(value)) : String(value)}</>;
}

/** the settings a package or template version sets — read only */
export function SettingsList({ map }: { map: SettingsMap }) {
  const t = useT();
  const entries = Object.entries(map);
  if (!entries.length) return <p className="text-sm text-ink-500">{t('Nothing set here — every value comes from the layer below.')}</p>;
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
      {entries.map(([k, v]) => {
        const { key, product } = splitSettingKey(k);
        return (
          <div key={k} className="contents">
            <dt className="text-ink-600">
              {t(settingDef(key)?.label ?? key)} <span className="font-mono text-[11px] text-ink-500">{product ?? (settingDef(key)?.scope === 'product' ? t('every product') : '')}</span>
            </dt>
            <dd className="text-right font-medium tabular-nums">
              <SettingValueText settingKey={key} value={v} />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** the editor of a package's or template's settings: add a value (for every product or for one), remove one */
export function SettingsEditor({ map, products, onChange }: { map: SettingsMap; products: string[]; onChange: (m: SettingsMap) => void }) {
  const t = useT();
  const [key, setKey] = useState(SETTINGS[0]!.key);
  const [product, setProduct] = useState('');
  const [value, setValue] = useState('');
  const def = settingDef(key)!;
  const parsed: number | string = def.kind === 'int' ? Number(value) : value;
  const problem = value === '' && def.kind === 'int' ? t('Type a value') : settingProblem(def, parsed);
  const add = () => {
    onChange({ ...map, [def.scope === 'product' && product ? `${key}@${product}` : key]: parsed });
    setValue('');
  };
  const remove = (k: string) => {
    const next = { ...map };
    delete next[k];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      <SettingsList map={map} />
      {Object.keys(map).length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {Object.keys(map).map((k) => (
            <li key={k}>
              <Button variant="ghost" icon={<Trash2 size={12} />} onClick={() => remove(k)} className="h-7 px-2 text-xs" aria-label={`${t('Remove')} ${k}`}>
                {k}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2 border-t border-ink-100 pt-3">
        <Select value={key} onChange={(e) => setKey(e.target.value)} aria-label={t('Setting')}>
          {SETTINGS.map((s) => (
            <option key={s.key} value={s.key}>
              {t(s.label)}
            </option>
          ))}
        </Select>
        {def.scope === 'product' && (
          <Select value={product} onChange={(e) => setProduct(e.target.value)} aria-label={t('For')}>
            <option value="">{t('every product')}</option>
            {products.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        )}
        <Input value={value} onChange={(e) => setValue(e.target.value)} type={def.kind === 'int' ? 'number' : 'text'} className="w-40" aria-label={t('Value')} placeholder={t('Value')} />
        <Button variant="secondary" icon={<Plus size={14} />} onClick={add} disabled={!!problem}>
          {t('Set')}
        </Button>
        {value !== '' && problem && <span className="text-xs text-red-600">{problem}</span>}
      </div>
      <p className="text-xs text-ink-500">{t(def.help)}</p>
    </div>
  );
}
